import React, { useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";

const COLORS = [
  "#8B5CF6", "#EC4899", "#1FAF5C", "#3B82F6", "#F59E0B",
  "#EF4444", "#06B6D4", "#84CC16", "#F97316", "#6366F1",
  "#14B8A6", "#A855F7", "#F43F5E", "#0EA5E9", "#D946EF",
];

const CustomTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className='bg-white rounded-xl border border-border-subtle shadow-lg px-4 py-3'>
      <p className='text-body-xs font-semibold text-text-primary mb-1.5'>
        {d.display_name || d.name}
      </p>
      <div className='space-y-1 text-body-xs'>
        <div className='flex justify-between gap-4'>
          <span className='text-text-secondary'>Cost</span>
          <span className='font-semibold text-text-primary'>${d.cost_usd?.toFixed(2)}</span>
        </div>
        <div className='flex justify-between gap-4'>
          <span className='text-text-secondary'>Calls</span>
          <span className='font-semibold text-text-primary'>{d.call_count?.toLocaleString()}</span>
        </div>
        <div className='flex justify-between gap-4'>
          <span className='text-text-secondary'>Input tokens</span>
          <span className='font-semibold text-text-primary'>{(d.input_tokens / 1_000_000).toFixed(2)}M</span>
        </div>
        <div className='flex justify-between gap-4'>
          <span className='text-text-secondary'>Output tokens</span>
          <span className='font-semibold text-text-primary'>{(d.output_tokens / 1_000).toFixed(1)}K</span>
        </div>
      </div>
    </div>
  );
};

const LABELS = {
  purpose: "Spending by Purpose",
  caller: "Spending by Caller",
  model: "Spending by Model",
  env: "Spending by Env",
  website: "Spending by Website",
  scan: "Spending by Scan",
};

// Date range and all filters come from the page so every chart and the scan
// table answer the same question — per-chart period toggles made the three
// breakdowns silently disagree with the trends chart and the table totals.
const CostBreakdownChart = ({ data = [], isLoading = false, error = null, groupBy = "purpose" }) => {
  const chartItems = useMemo(() => {
    return data.slice(0, 15).map((d) => ({
      ...d,
      label: (d.display_name || d.name || "unknown").length > 25
        ? (d.display_name || d.name).slice(0, 22) + "..."
        : d.display_name || d.name,
    }));
  }, [data]);

  const totalCost = useMemo(
    () => data.reduce((s, d) => s + (d.cost_usd || 0), 0),
    [data],
  );

  const chartHeight = Math.max(250, chartItems.length * 36 + 40);

  return (
    <div>
      <div className='flex items-center justify-between mb-4'>
        <h2 className='text-body-lg font-semibold text-text-primary'>{LABELS[groupBy] || `Spending by ${groupBy}`}</h2>
        {totalCost > 0 && (
          <span className='text-body-xs font-semibold text-text-secondary'>
            ${totalCost.toFixed(2)}
          </span>
        )}
      </div>

      {isLoading ? (
        <div className='h-[300px] flex items-center justify-center'>
          <div className='flex flex-col items-center gap-2'>
            <div className='animate-spin rounded-full h-6 w-6 border-2 border-accent-purple border-t-transparent' />
            <span className='text-body-sm text-text-tertiary'>Loading breakdown...</span>
          </div>
        </div>
      ) : error ? (
        <div className='h-[300px] flex items-center justify-center'>
          <p className='text-body-sm text-severity-high'>Failed to load breakdown</p>
        </div>
      ) : !chartItems.length ? (
        <div className='h-[300px] flex items-center justify-center'>
          <p className='text-body-sm text-text-tertiary'>No breakdown data available</p>
        </div>
      ) : (
        <div style={{ height: chartHeight }}>
          <ResponsiveContainer width='100%' height='100%'>
            <BarChart data={chartItems} layout='vertical' margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray='3 3' stroke='rgba(10,16,48,0.06)' horizontal={false} />
              <XAxis
                type='number'
                tick={{ fontSize: 11, fill: "rgba(10,10,38,0.4)" }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => `$${v}`}
              />
              <YAxis
                type='category'
                dataKey='label'
                width={180}
                tick={{ fontSize: 11, fill: "rgba(10,10,38,0.6)" }}
                axisLine={false}
                tickLine={false}
              />
              <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(139,92,246,0.04)" }} />
              <Bar dataKey='cost_usd' radius={[0, 6, 6, 0]} barSize={20}>
                {chartItems.map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export default CostBreakdownChart;
